import { useCallback, useMemo, useRef, memo, ReactElement } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Guard, useGuard } from '@ohpen/react-authz';
import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';

import { ActionEntityType, BaseRateResponse, InterestRate, InterestRateRequest, VariabilityType } from '@pmp/api';
import { useListTableState, useTableView } from '@pmp/hooks';
import { formatCompactDate, formatDate, lockActionTitle } from '@pmp/utils';
import { useExportTemplatesStore } from '@pmp/stores';

import { baseRateColumns, historicColumns } from '../tableConfig';
import { BaseRateListProps, InterestRateDelete } from '../interfaces';

const entityType = ActionEntityType.BASE_RATES;
const rowKey = 'code';

export const BaseRatesList = memo(
  ({
    isFetching,
    valueLists,
    data,
    onCreateBaseRate,
    onView,
    onEditBaseRate,
    onDeleteBaseRate,
    onCreateInterestRate,
    onEditInterestRate,
    onDeleteInterestRate,
  }: BaseRateListProps): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { tableViews, saveTableView, deleteTableView } = useTableView(entityType);
    const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
    const { t } = useTranslation();
    const { allowed: canWrite } = useGuard({
      permissions: ['baseRates:update'],
    });
    const tableState = useListTableState<BaseRateResponse>(entityType, rowKey, tableRef, baseRateColumns);

    const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.BASE_RATES), [getExportTemplates]);

    const createInterestRate = useCallback(
      (code: string) => {
        const data = {
          baseRateCode: code,
        } as InterestRateRequest;

        onCreateInterestRate(data);
      },
      [onCreateInterestRate],
    );

    const editInterestRate = useCallback(
      (record: InterestRate, parentRecord: BaseRateResponse) => {
        const data: InterestRateRequest = {
          baseRateCode: parentRecord.code,
          amount: record.amount,
          index: record.index,
          startDate: record.startDate,
        };

        onEditInterestRate(data);
      },
      [onEditInterestRate],
    );

    const deleteInterestRate = useCallback(
      (record: InterestRate, parentRecord: BaseRateResponse) => {
        const data: InterestRateDelete = {
          id: `${parentRecord.code}#${record.index}`,
          name: `${parentRecord.code} ${record.amount} ${formatCompactDate(record.startDate)}`,
        };

        onDeleteInterestRate(data);
      },
      [onDeleteInterestRate],
    );

    const getRowActions: (record: BaseRateResponse) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button type="text" aria-label="View base rate" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="export" permissions={['baseRates:update']}>
            <Tooltip key="edit" title={lockActionTitle('edit', record.lockedByActionId, t('pmp.baseRates.labels.edit'))} placement="top">
              <Button
                type="text"
                aria-label="Edit base rate"
                icon={<EditOutlined key="edit" />}
                onClick={() => onEditBaseRate(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
            <Tooltip
              key="create"
              title={
                record.variabilityType.key === VariabilityType.FIXED
                  ? t('pmp.baseRates.labels.fixedInterestRateTooltip')
                  : lockActionTitle('create', record.lockedByActionId, t('pmp.baseRates.labels.addInterestRate'))
              }
              placement="top"
            >
              <Button
                type="text"
                icon={<PlusCircleOutlined key="create" />}
                aria-label="Create new interest rate"
                onClick={() => createInterestRate(record.code)}
                disabled={record.variabilityType.key === VariabilityType.FIXED || !!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['baseRates:delete']}>
            <Tooltip key="delete" title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Delete base rate"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => onDeleteBaseRate(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
        ];

        return rowActions;
      },
      [onEditBaseRate, onDeleteBaseRate, onView, createInterestRate, t, valueLists],
    );

    const getExpandedRowActions: (record: InterestRate, parentRecord: BaseRateResponse) => ReactElement[] = useCallback(
      (record, parentRecord) => {
        const isActive = dayjs(record.startDate).isSame(dayjs(), 'day') || dayjs(record.startDate).isBefore(dayjs());

        return [
          <Guard key="export" permissions={['baseRates:update']}>
            <Tooltip key="edit" title={isActive || !!parentRecord.lockedByActionId ? '' : t('global.labels.edit')} placement="left">
              <Button
                type="text"
                aria-label="Edit interest rate"
                icon={<EditOutlined key="edit" />}
                onClick={() => editInterestRate(record, parentRecord)}
                disabled={isActive || !!parentRecord.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['baseRates:delete']}>
            <Tooltip key="delete" title={isActive || !!parentRecord.lockedByActionId ? '' : t('global.labels.delete')} placement="top">
              <Button
                type="text"
                aria-label="Delete interest rate"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => deleteInterestRate(record, parentRecord)}
                disabled={isActive || !!parentRecord.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
        ];
      },
      [deleteInterestRate, editInterestRate, t],
    );

    const tableProps: TableProps<ActionEntityType, BaseRateResponse, InterestRate> = useMemo(() => {
      const expandedColumns = canWrite ? historicColumns : historicColumns.filter(column => column.key !== 'actions');

      return {
        ...tableState,
        id: entityType,
        ref: tableRef,
        dataSource: data ?? [],
        columns: baseRateColumns,
        rowKey,
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        valueLists,
        loading: isFetching,
        getRowActions,
        expanded: {
          columns: expandedColumns,
          columnsKey: 'interestRates',
          rowKey: 'index',
          getRowActions: getExpandedRowActions,
        },
        showViews: true,
        tableViews,
        saveTableView,
        deleteTableView,
      };
    }, [
      tableState,
      canWrite,
      data,
      valueLists,
      isFetching,
      getRowActions,
      getExpandedRowActions,
      tableViews,
      saveTableView,
      deleteTableView,
    ]);

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  editableFileName
                  fileName={`Base rates ${formatDate()}`}
                  sheetName="Base rates"
                  disabled={isFetching}
                  loadingTemplates={isLoadingExportTemplates}
                  getTemplates={getTemplates}
                  modalDefaults={{ columnScope: 'template' }}
                  allowSearch={true}
                  exportView
                  flatten
                />
              </Guard>
              {canWrite && <Table.CreateButton onClick={onCreateBaseRate} />}
            </Space>
          </Flex>
        </Table.ActionsBar>
      </Table>
    );
  },
);
